/**
 * /app/admin/bot-research
 *
 * Internal-only forensic view. Built in response to the 2026-05-17
 * incident where a customer's link (go.poof.bio/camilacow) triggered
 * an Instagram account limit and we had no per-request IP/ASN/UA
 * data to investigate. Now every served request is captured in the
 * `poof_observations` Analytics Engine dataset and surfaced here.
 *
 * Gating: only renders for users in the ADMIN_EMAILS allowlist
 * (DashboardLayout hides the nav; the API endpoint re-checks server-
 * side via requireAdmin). Non-admins who type the URL directly get a
 * 403 from /api/admin/bot-research and an empty page.
 *
 * Never expose any of this to customers — UA strings, ASN numbers, and
 * the headers blob are detection-methodology surface; customers see
 * only aggregated counts in their Poof Shield tab.
 */

import { useEffect, useMemo, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { AdminGuard } from '@/components/AdminGuard';
import { AdminSubNav } from '@/components/AdminSubNav';
import { admin, ApiError, type BotResearchResponse, type BotResearchRow } from '@/lib/api';
import { Button, Surface } from '@/components/ui';

const DEFAULT_HOST = 'go.poof.bio';
const META_ASNS = new Set(['AS32934', 'AS54115', 'AS63293']); // Facebook, Instagram, Meta CDN

export default function AdminBotResearch() {
  return (
    <AdminGuard>
      <AdminBotResearchContent />
    </AdminGuard>
  );
}

function AdminBotResearchContent() {
  const [host, setHost] = useState(DEFAULT_HOST);
  const [path, setPath] = useState('');
  const [days, setDays] = useState(7);
  const [data, setData] = useState<BotResearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function search(e?: React.FormEvent) {
    e?.preventDefault();
    if (!path.trim()) {
      setErr('Enter a path (e.g. /camilacow).');
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      const normalizedPath = path.trim().startsWith('/') ? path.trim() : `/${path.trim()}`;
      const r = await admin.botResearch(host.trim(), normalizedPath, days, 1000);
      setData(r);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setErr("You're not in the admin allowlist. This view is internal-only.");
      } else if (e instanceof ApiError && e.status === 401) {
        setErr('Not signed in.');
      } else {
        setErr('Failed to load. Check that the host/path are correct.');
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  return (
    <DashboardLayout title="Bot research" wide>
      <AdminSubNav />
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">🔬 Bot research</h1>
          <p className="mt-1 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
            Per-request forensic log — IP hash, ASN, User-Agent, headers, and
            classification for every visit to a given (host, path). Used to
            investigate incidents like the 2026-05-17 IG account-limit case.{' '}
            <strong>Internal only. Never surface any of this to customers.</strong>
          </p>
        </div>

        <Surface tone="raised" className="p-5">
        <form onSubmit={search} className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <label className="block text-[10px] font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
              Host
            </label>
            <input
              type="text"
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="go.poof.bio"
              className="mt-1 block w-full rounded-xl border border-[var(--color-border)] bg-[var(--surface-deep)] px-3 py-2 text-sm outline-none focus:border-[var(--color-ring)]"
            />
          </div>
          <div className="flex-[2] min-w-[200px]">
            <label className="block text-[10px] font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
              Path
            </label>
            <input
              type="text"
              value={path}
              onChange={(e) => setPath(e.target.value)}
              placeholder="/camilacow"
              autoFocus
              className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-transparent px-3 py-2 font-mono text-sm outline-none focus:border-[var(--color-ring)]"
            />
          </div>
          <div className="w-28">
            <label className="block text-[10px] font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
              Days
            </label>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="mt-1 block w-full rounded-xl border border-[var(--color-border)] bg-[var(--surface-deep)] px-3 py-2 text-sm outline-none focus:border-[var(--color-ring)]"
            >
              <option value={1}>1</option>
              <option value={3}>3</option>
              <option value={7}>7</option>
              <option value={14}>14</option>
              <option value={30}>30</option>
            </select>
          </div>
          <Button type="submit" disabled={loading}>
            {loading ? 'Loading…' : 'Search'}
          </Button>
        </form>
        </Surface>

        {err && (
          <div className="rounded-lg border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/5 p-3 text-sm text-[var(--color-destructive)]">
            {err}
          </div>
        )}

        {data && <Summary data={data} />}
        {data && <RowsTable rows={data.rows} />}
        {data && data.rows.length === 0 && (
          <div className="rounded-xl border border-dashed border-[var(--color-border)] p-8 text-center text-sm text-[var(--color-muted-foreground)]">
            No observations recorded yet for {data.host}
            {data.path} in the last {data.days} days.
            <br />
            <span className="text-xs">
              If you expected hits here, confirm the bouncer is deployed with the
              OBSERVATIONS binding and that the request is hitting this exact path.
            </span>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

// ---------------------------------------------------------------------------
// Top-of-page aggregate summary — what to read first when investigating.
// ---------------------------------------------------------------------------

function Summary({ data }: { data: BotResearchResponse }) {
  const metaCount = useMemo(
    () => data.byAsn.filter((a) => META_ASNS.has(a.asn)).reduce((s, a) => s + a.count, 0),
    [data.byAsn],
  );
  const suspiciousCount = useMemo(
    () => data.rows.filter((r) => r.isSuspicious).reduce((s) => s + 1, 0),
    [data.rows],
  );
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <SummaryCard
        title="Total requests"
        value={data.total.toLocaleString()}
        sub={`last ${data.days} days · ${data.rows.length} unique rows`}
      />
      <SummaryCard
        title="Meta-ASN hits"
        value={metaCount.toLocaleString()}
        sub="AS32934 / AS54115 / AS63293"
        highlight={metaCount > 0}
      />
      <SummaryCard
        title="Bot-flagged UAs"
        value={suspiciousCount.toLocaleString()}
        sub="isSuspicious() = true"
        highlight={suspiciousCount > 0}
      />
      <BreakdownCard title="By action" rows={data.byAction.map((r) => ({ label: r.actionKind, count: r.count }))} />
      <BreakdownCard
        title="By classification"
        rows={data.byClassification.map((r) => ({ label: r.shieldClass, count: r.count }))}
      />
      <BreakdownCard
        title="Top ASNs"
        rows={data.byAsn.slice(0, 10).map((r) => ({
          label: r.asn || '(none)',
          count: r.count,
          highlight: META_ASNS.has(r.asn),
        }))}
      />
    </div>
  );
}

function SummaryCard({
  title,
  value,
  sub,
  highlight,
}: {
  title: string;
  value: string;
  sub: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={
        'rounded-xl border p-4 ' +
        (highlight
          ? 'border-[var(--color-destructive)] bg-[var(--color-destructive)]/5'
          : 'border-[var(--color-border)]')
      }
    >
      <div className="text-xs uppercase tracking-wider text-[var(--color-muted-foreground)]">
        {title}
      </div>
      <div className="mt-1 text-3xl font-semibold tabular-nums">{value}</div>
      <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">{sub}</div>
    </div>
  );
}

function BreakdownCard({
  title,
  rows,
}: {
  title: string;
  rows: Array<{ label: string; count: number; highlight?: boolean }>;
}) {
  if (rows.length === 0) {
    return (
      <div className="rounded-2xl glass p-4">
        <div className="text-xs uppercase tracking-wider text-[var(--color-muted-foreground)]">
          {title}
        </div>
        <div className="mt-2 text-xs text-[var(--color-muted-foreground)]">No data.</div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl glass p-4">
      <div className="text-xs uppercase tracking-wider text-[var(--color-muted-foreground)]">
        {title}
      </div>
      <ul className="mt-2 space-y-1 text-sm">
        {rows.map((r) => (
          <li
            key={r.label}
            className={
              'flex items-baseline justify-between gap-3 ' +
              (r.highlight ? 'text-[var(--color-destructive)] font-medium' : '')
            }
          >
            <span className="truncate font-mono text-xs">{r.label}</span>
            <span className="tabular-nums">{r.count.toLocaleString()}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row table — one per unique (timestamp, ip_hash, ua, ...) tuple.
// ---------------------------------------------------------------------------

function RowsTable({ rows }: { rows: BotResearchRow[] }) {
  const [expanded, setExpanded] = useState<number | null>(null);
  if (rows.length === 0) return null;
  return (
    <div className="overflow-x-auto rounded-xl border border-[var(--color-border)]">
      <table className="w-full min-w-[1100px] text-xs">
        <thead className="bg-[var(--color-muted)]/40 text-left">
          <tr>
            <Th>Time</Th>
            <Th>Country</Th>
            <Th>ASN</Th>
            <Th>Action</Th>
            <Th>Class</Th>
            <Th>Bot</Th>
            <Th>CFScore</Th>
            <Th>UA</Th>
            <Th>IP hash</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const isMeta = META_ASNS.has(r.asn);
            return (
              <RowDisclosure
                key={i}
                row={r}
                isOpen={expanded === i}
                onToggle={() => setExpanded(expanded === i ? null : i)}
                isMeta={isMeta}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">
      {children}
    </th>
  );
}

function RowDisclosure({
  row,
  isOpen,
  onToggle,
  isMeta,
}: {
  row: BotResearchRow;
  isOpen: boolean;
  onToggle: () => void;
  isMeta: boolean;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        className={
          'cursor-pointer border-t border-[var(--color-border)] hover:bg-[var(--color-muted)]/30 ' +
          (isMeta ? 'bg-[var(--color-destructive)]/5' : '')
        }
      >
        <td className="px-3 py-2 font-mono whitespace-nowrap">{row.timestamp.replace('T', ' ').replace('Z', '')}</td>
        <td className="px-3 py-2">{row.country || '—'}</td>
        <td className={'px-3 py-2 font-mono ' + (isMeta ? 'font-semibold text-[var(--color-destructive)]' : '')}>
          {row.asn || '—'}
        </td>
        <td className="px-3 py-2">{row.actionKind}</td>
        <td className="px-3 py-2 font-mono">{row.shieldClass}</td>
        <td className="px-3 py-2">
          {row.isSuspicious ? (
            <span className="rounded bg-[var(--color-destructive)]/20 px-1.5 py-0.5 text-[10px] font-medium text-[var(--color-destructive)]">
              bot
            </span>
          ) : (
            <span className="text-[var(--color-muted-foreground)]">—</span>
          )}
        </td>
        <td className="px-3 py-2 tabular-nums">{row.cfBotScore ?? '—'}</td>
        <td className="px-3 py-2 max-w-[280px] truncate font-mono" title={row.userAgent}>
          {row.userAgent.slice(0, 60)}
          {row.userAgent.length > 60 ? '…' : ''}
        </td>
        <td className="px-3 py-2 font-mono">{row.ipHash.slice(0, 12)}…</td>
      </tr>
      {isOpen && (
        <tr className="border-t border-[var(--color-border)] bg-[var(--color-muted)]/20">
          <td colSpan={9} className="px-4 py-3">
            <div className="grid gap-4 md:grid-cols-2">
              <DetailField label="Full User-Agent">
                <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-[11px]">{row.userAgent || '(empty)'}</pre>
              </DetailField>
              <DetailField label="Headers">
                {Object.keys(row.headers).length === 0 ? (
                  <span className="text-[var(--color-muted-foreground)]">(none captured)</span>
                ) : (
                  <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-[11px]">
                    {Object.entries(row.headers)
                      .map(([k, v]) => `${k}: ${v}`)
                      .join('\n')}
                  </pre>
                )}
              </DetailField>
              <DetailField label="IP hash (full)">
                <code className="break-all font-mono text-[11px]">{row.ipHash}</code>
              </DetailField>
              <DetailField label="Metadata">
                <ul className="space-y-1 text-[11px]">
                  <li>
                    <span className="text-[var(--color-muted-foreground)]">Host-app classification:</span>{' '}
                    <code>{row.hostApp || '—'}</code>
                  </li>
                  <li>
                    <span className="text-[var(--color-muted-foreground)]">EU:</span>{' '}
                    <code>{row.isEu ? 'yes' : 'no'}</code>
                  </li>
                  <li>
                    <span className="text-[var(--color-muted-foreground)]">Cloudflare bot score:</span>{' '}
                    <code>{row.cfBotScore ?? 'not available'}</code>
                  </li>
                  <li>
                    <span className="text-[var(--color-muted-foreground)]">Org id:</span>{' '}
                    <code>{row.orgId || '—'}</code>
                  </li>
                  <li>
                    <span className="text-[var(--color-muted-foreground)]">Link id:</span>{' '}
                    <code>{row.linkId || '—'}</code>
                  </li>
                </ul>
              </DetailField>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function DetailField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">
        {label}
      </div>
      <div className="mt-1">{children}</div>
    </div>
  );
}
